import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Download, ImageIcon, Inbox, Loader2, Search } from "lucide-react";
import { toast } from "sonner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { SelectInput, TextInput } from "@/components/inquiry/fields";
import { api, formatApiError } from "@/lib/api";
import { LEAD_STATUS, LEAD_TYPES, causeLabel } from "@/lib/leadLabels";
import { cn } from "@/lib/utils";
import { fmtDate, StatusBadge, TypeBadge } from "@/pages/admin/badges";

const useDebounced = (value, ms = 300) => {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
};

const detail = (l) => l.concern || l.service || (l.cause && causeLabel(l.cause)) || "–";

const StatCards = ({ counts, status, setStatus }) => (
  <div className="grid gap-3 sm:grid-cols-3">
    {Object.entries(LEAD_STATUS).map(([key, label]) => (
      <button key={key} type="button" onClick={() => setStatus(status === key ? "" : key)} data-testid={`admin-stat-${key}`}
        className={cn("rounded-2xl bg-white p-5 text-left shadow-soft ring-1 transition-[box-shadow,transform] hover:-translate-y-0.5",
          status === key ? "ring-2 ring-brand-blue" : "ring-slate-100")}>
        <span className="block text-sm font-semibold text-brand-ink">{label}</span>
        <span className="mt-1 block text-3xl font-bold text-brand-anthracite" data-testid={`admin-stat-${key}-count`}>{counts?.[key] ?? 0}</span>
      </button>
    ))}
  </div>
);

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [type, setType] = useState("");
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const q = useDebounced(search);
  const params = { ...(type && { type }), ...(status && { status }), ...(q && { q }) };

  const { data, isLoading, isError } = useQuery({
    queryKey: ["leads", params],
    queryFn: () => api.get("/admin/leads", { params }).then((r) => r.data),
  });

  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/leads/export.csv", { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `anfragen-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-brand-anthracite">Anfragen</h1>
          <p className="text-sm">Rückrufe, E-Mail-Anfragen und Schadenmeldungen. E-Mail-Benachrichtigungen folgen in Phase 2.</p>
        </div>
        <button type="button" onClick={exportCsv} data-testid="admin-export-csv"
          className="inline-flex items-center gap-2 rounded-full bg-brand-blue px-5 py-2.5 text-sm font-bold text-white shadow-soft transition-colors hover:bg-brand-blue-dark">
          <Download className="h-4 w-4" /> CSV-Export
        </button>
      </div>

      <StatCards counts={data?.counts} status={status} setStatus={setStatus} />

      <div className="grid gap-3 rounded-2xl bg-white p-4 shadow-soft ring-1 ring-slate-100 sm:grid-cols-[1fr_200px_200px]">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <TextInput value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Suche: Name, Telefon, E-Mail, Kennzeichen, Vorgang" className="pl-9" data-testid="admin-search-input" />
        </div>
        <SelectInput value={type} onChange={(e) => setType(e.target.value)} data-testid="admin-filter-type">
          <option value="">Alle Arten</option>
          {Object.entries(LEAD_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </SelectInput>
        <SelectInput value={status} onChange={(e) => setStatus(e.target.value)} data-testid="admin-filter-status">
          <option value="">Alle Status</option>
          {Object.entries(LEAD_STATUS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </SelectInput>
      </div>

      <div className="overflow-hidden rounded-2xl bg-white shadow-soft ring-1 ring-slate-100">
        {isLoading ? (
          <div className="grid place-items-center py-16"><Loader2 className="h-6 w-6 animate-spin text-brand-blue" /></div>
        ) : isError ? (
          <p className="p-8 text-center text-red-600" data-testid="admin-leads-error">Anfragen konnten nicht geladen werden.</p>
        ) : data.items.length === 0 ? (
          <div className="py-16 text-center" data-testid="admin-leads-empty">
            <Inbox className="mx-auto h-8 w-8 text-slate-300" />
            <p className="mt-2 font-semibold text-brand-anthracite">Keine Anfragen gefunden</p>
          </div>
        ) : (
          <Table data-testid="admin-leads-table">
            <TableHeader>
              <TableRow className="bg-brand-mist hover:bg-brand-mist">
                <TableHead>Eingang</TableHead><TableHead>Art</TableHead><TableHead>Name</TableHead><TableHead>Kontakt</TableHead>
                <TableHead>Details</TableHead><TableHead>Herkunft</TableHead><TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.items.map((l) => (
                <TableRow key={l.id} onClick={() => navigate(`/admin/anfragen/${l.id}`)} className="cursor-pointer" data-testid={`admin-lead-row-${l.id}`}>
                  <TableCell className="whitespace-nowrap text-sm">{fmtDate(l.created_at)}<span className="block text-xs text-slate-500">{l.ref}</span></TableCell>
                  <TableCell><TypeBadge type={l.type} /></TableCell>
                  <TableCell className="font-semibold text-brand-anthracite">{l.name}</TableCell>
                  <TableCell className="text-sm">{l.phone || "–"}<span className="block text-xs text-slate-500">{l.email}</span></TableCell>
                  <TableCell className="text-sm">
                    {detail(l)}
                    {l.photos.length > 0 && <span className="ml-2 inline-flex items-center gap-1 text-xs font-semibold text-brand-blue"><ImageIcon className="h-3.5 w-3.5" />{l.photos.length}</span>}
                  </TableCell>
                  <TableCell className="max-w-[160px] truncate text-xs text-slate-500">{l.source_page}{l.utm_source && ` · ${l.utm_source}`}{l.gclid && " · Ads"}</TableCell>
                  <TableCell><StatusBadge status={l.status} testId={`admin-lead-status-${l.id}`} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
